/**
 * Native ES module produced from the historical shared-scope controller.
 * Cross-domain dependencies are explicit imports; eager DOM wiring is isolated in the init function.
 */
import { publishViewSnapshot } from '../../stores/viewStore.js';
import { ensureInterfaceStyle } from '../../styles/lazy.js';
import { preloadView } from '../lazy/loaders.js';
import { openNote, renderEvents, renderNavigator, renderNotes, renderSolutions, renderStudentCouncil } from '../community/events-notes-navigator.runtime.js';
import { renderCourseSpace, renderCourses, renderSemester, renderSimpleStudyHub } from '../courses/courses.runtime.js';
import { renderAdmin, renderGroup, renderNotifications, renderUITextEditor } from '../groups/groups-notifications.runtime.js';
import { renderAttendance, renderMaterials, renderTeachers, renderWiki } from '../knowledge/materials-attendance-wiki.runtime.js';
import { applyAccentColor, applyGlassOriginFromClick, applyNavigationOrder, applyTheme, captureGlassOrigin, currentThemePreference, ensureSuperGrokChrome, glassClickOrigin, glassFlowHold, setGlassFlowHold, isRussianInterfaceStyle, normalizeInterfaceMode, normalizeInterfacePreferences, normalizeInterfaceStyle, openOnboardingWizardV13, resolveTheme, saveLocalInterfacePreferences, scheduleGlassOriginApply, setGlassFlow, setThemePreference, syncSuperGrokChrome } from '../onboarding/onboarding.runtime.js';
import { contextSkeleton, openSimpleAddMenu, renderContextHome, renderContextMoreHub, renderContextStudyHub, renderSuperGrokHome, renderSuperGrokMoreHub, renderSuperGrokSchedule, renderSuperGrokStudyHub, uiIcon } from '../platform/android-uikit.runtime.js';
import { $, $$, API, api, availableSectionViews, availableSimpleViews, compactMode, contextMode, escapeHTML, isAdmin, isProjectAdmin, parseDeepLink, sectionForView, showLegalPage, simpleParentView, state, superGrokMode, syncViewURL, t, titleFor, titleKeys, toast, trackProduct, setRouterSilent } from '../platform/core.runtime.js';
import { closeDialog, openDialog } from '../platform/dialog-boot.runtime.js';
import { renderSkipLesson, showSchedule } from '../schedule/lesson.runtime.js';
import { canManageEvents, canManageGroup, canUploadNotes, currentGroupLabel, openGroupSwitcher, renderGroupSelector, selectGroup } from '../session/session.runtime.js';
import { mountWindowSwipe, renderSchedule, renderSettings, renderSimpleMoreHub } from '../settings/settings-schedule.runtime.js';
import { renderContextCourseSpace, renderContextCourses, renderContextLesson, renderContextSchedule, renderContextSemester, renderSimpleHome } from './hubs.runtime.js';
import { renderHomework } from '../study/homework-ai.runtime.js';
import { renderRequirements } from '../study/requirements.runtime.js';

export function enterGlassShell({ menu = false, showOrb = false } = {}) {
  const dialog = $('#dialog');
  if (!dialog) return null;
  setGlassFlow(true);
  dialog.classList.add('sg-orb-dialog', 'sg-sheet-dialog');
  dialog.classList.toggle('sg-glass-form', !menu);
  dialog.classList.toggle('sg-glass-menu', menu);
  const kicker = dialog.querySelector('.dialog-kicker');
  const header = dialog.querySelector('header');
  const footer = $('#dialog-footer');
  if (menu) {
    if (kicker) kicker.hidden = true;
    if (header) header.hidden = true;
    if (footer) footer.hidden = true;
  } else {
    if (kicker) kicker.hidden = true;
    if (header) header.hidden = false;
    if (footer) footer.hidden = footer.childElementCount === 0;
    // Hide default X — glass uses back-feeling close via orb / backdrop / footer
    const closeBtn = $('#dialog-close');
    if (closeBtn) closeBtn.hidden = true;
  }
  if (showOrb && menu) {
    const sheet = dialog.querySelector('.sg-orb-sheet');
    if (sheet && !sheet.querySelector('.sg-orb-stay')) {
      const orb = document.createElement('button');
      orb.type = 'button';
      orb.className = 'sg-orb-stay is-open';
      orb.setAttribute('data-sg-sheet-close', '');
      orb.setAttribute('aria-label', 'Закрыть');
      orb.innerHTML = '<span class="sg-orb-core">+</span>';
      orb.addEventListener('click', () => closeOrbSheet());
      sheet.append(orb);
    }
  }
  $('#sg-orb')?.classList.add('is-open');
  document.body.classList.add('sg-sheet-active');
  dialog.classList.remove('sg-sheet-closing');
  // Already-open glass (morph / form swap): keep opacity 1 — no reopen flash.
  if (dialog.open) {
    dialog.classList.add('sg-sheet-open');
    scheduleGlassOriginApply(dialog);
  } else {
    requestAnimationFrame(() => {
      dialog.classList.add('sg-sheet-open');
      scheduleGlassOriginApply(dialog);
    });
  }
  return dialog;
}

function glassMenuActionsHTML(items = []) {
  const n = items.length;
  return items.map((a, i) => {
    // Snappier, less staggered — avoids laggy cascade.
    const dur = (0.22 + Math.min(i, 4) * 0.018).toFixed(3);
    const delay = 6 + i * 16;
    return `<button type="button" class="sg-orb-action sg-from-origin" role="menuitem" data-glass-action="${escapeHTML(a.id)}" style="--sg-i:${i};--sg-n:${n};--sg-dur:${dur}s;--sg-delay:${delay}ms"><span class="sg-orb-ico" aria-hidden="true">${a.icon || '✦'}</span><span class="sg-orb-copy"><strong>${escapeHTML(a.label)}</strong><small>${escapeHTML(a.meta || '')}</small></span><i aria-hidden="true">→</i></button>`;
  }).join('');
}

function wireGlassMenuActions(dialog, items = []) {
  if (!dialog) return;
  $$('[data-glass-action]', dialog).forEach((button) => {
    button.addEventListener('click', async (event) => {
      const item = items.find((a) => a.id === button.dataset.glassAction);
      if (!item) return;
      captureGlassOrigin(event.currentTarget || button);
      try {
        if (item.stayInGlass) {
          // Keep the glass stage; nested menu morphs or form reuses shell.
          setGlassFlow(true);
          setGlassFlowHold(true);
          await item.run?.(event.currentTarget || button);
          return;
        }
        await closeOrbSheet();
        await item.run?.(event.currentTarget || button);
      } catch (error) {
        toast(error.message);
      }
    });
  });
}

export /**
 * In-place menu swap: no dialog.close flash.
 * Rows below the press fade out; head + new rows arrive from the pressed control.
 */
function morphGlassMenu({ kicker = 'Быстрый переход', title = 'Куда?', items = [], showOrb = true, fromEl = null } = {}) {
  const dialog = $('#dialog');
  const panel = dialog?.querySelector('.sg-orb-sheet-panel');
  const sheet = dialog?.querySelector('.sg-orb-sheet');
  if (!dialog?.open || !panel || !dialog.classList.contains('sg-glass-menu')) {
    return openGlassMenu({ kicker, title, items, showOrb });
  }
  setGlassFlow(true);
  if (fromEl) captureGlassOrigin(fromEl);
  else if (!glassClickOrigin) captureGlassOrigin(panel.querySelector('.sg-orb-action'));

  const head = panel.querySelector('.sg-sheet-head');
  const actions = panel.querySelector('.sg-orb-actions');
  const rows = $$('.sg-orb-action', panel);
  const pressedId = fromEl?.dataset?.glassAction || '';

  rows.forEach((row, i) => {
    const isPressed = pressedId && row.dataset.glassAction === pressedId;
    if (isPressed) {
      row.classList.add('sg-morph-hold');
    } else {
      // Short cascade: cap stagger so bottom rows don't wait long.
      row.style.setProperty('--sg-morph-i', String(Math.min(3, Math.max(0, rows.length - 1 - i))));
      row.classList.add('sg-morph-out');
    }
  });
  head?.classList.add('sg-morph-hold');

  const OUT_MS = 100;
  window.clearTimeout(morphGlassMenu._timer);
  morphGlassMenu._timer = window.setTimeout(() => {
    if (!dialog.open || !panel.isConnected) return;
    const n = items.length;
    if (sheet) sheet.style.setProperty('--sg-n', String(n));
    if (head) {
      head.classList.remove('sg-morph-hold');
      head.className = 'sg-sheet-head sg-from-origin';
      head.style.setProperty('--sg-i', '0');
      head.innerHTML = `<span class="sg-sheet-kicker">${escapeHTML(kicker)}</span><strong>${escapeHTML(title)}</strong>`;
    }
    if (actions) {
      actions.innerHTML = glassMenuActionsHTML(items);
    }
    const titleNode = $('#dialog-title');
    if (titleNode) titleNode.textContent = title;
    if (showOrb && sheet && !sheet.querySelector('.sg-orb-stay')) {
      const orb = document.createElement('button');
      orb.type = 'button';
      orb.className = 'sg-orb-stay is-open';
      orb.setAttribute('data-sg-sheet-close', '');
      orb.setAttribute('aria-label', 'Закрыть');
      orb.innerHTML = '<span class="sg-orb-core">+</span>';
      orb.addEventListener('click', () => closeOrbSheet());
      sheet.append(orb);
    }
    wireGlassMenuActions(dialog, items);
    // Apply origin immediately after DOM swap (layout is ready).
    applyGlassOriginFromClick(dialog);
  }, OUT_MS);
  return dialog;
}

export function openGlassMenu({ kicker = 'Быстрый переход', title = 'Куда?', items = [], showOrb = true } = {}) {
  setGlassFlow(true);
  const n = items.length;
  const sheetHTML = `<div class="sg-orb-sheet" data-sg-sheet style="--sg-n:${n}">
    <div class="sg-orb-sheet-panel">
      <div class="sg-sheet-grabber" data-sg-sheet-grab aria-hidden="true"><span></span></div>
      <header class="sg-sheet-head sg-from-origin" style="--sg-i:0">
        <span class="sg-sheet-kicker">${escapeHTML(kicker)}</span>
        <strong>${escapeHTML(title)}</strong>
      </header>
      <div class="sg-orb-actions" role="menu">${glassMenuActionsHTML(items)}</div>
    </div>
    ${showOrb ? `<button type="button" class="sg-orb-stay is-open" data-sg-sheet-close aria-label="Закрыть"><span class="sg-orb-core">+</span></button>` : ''}
  </div>`;
  openDialog(title, sheetHTML, []);
  const dialog = enterGlassShell({ menu: true, showOrb });
  $$('[data-sg-sheet-close]', dialog).forEach((button) => {
    button.onclick = () => closeOrbSheet();
  });
  wireGlassMenuActions(dialog, items);
  wireOrbSheetGesture(dialog);
  scheduleGlassOriginApply(dialog);
  return dialog;
}

function orbIcon(name) {
  if (typeof uiIcon === 'function') return uiIcon(name, 21);
  return '✦';
}

function openOrbSection(section, fromEl = null) {
  const sections = {
    study: {
      kicker: 'Учёба',
      title: 'Что открыть?',
      items: [
        { id: 'homework', label: 'Задания', meta: 'Домашка и дедлайны', icon: orbIcon('tasks'), run: () => setView('homework') },
        { id: 'solutions', label: 'Решения', meta: 'Готовые и AI-решения', icon: orbIcon('solution'), run: () => setView('solutions') },
        { id: 'notes', label: 'Конспекты', meta: 'Файлы и записи', icon: orbIcon('file'), run: () => setView('notes') },
        { id: 'materials', label: 'Материалы', meta: 'Ссылки, книги и файлы', icon: orbIcon('link'), run: () => setView('materials') },
        { id: 'requirements', label: 'Автомат', meta: 'Условия по предметам', icon: orbIcon('target'), run: () => setView('requirements') },
      ],
    },
    schedule: {
      kicker: 'Расписание',
      title: 'Пары и посещение',
      items: [
        { id: 'schedule', label: 'Расписание', meta: 'Пары на неделю', icon: orbIcon('calendar'), run: () => setView('schedule') },
        { id: 'skip', label: 'Пропуск', meta: 'Сообщить, что не придёте', icon: orbIcon('skip'), run: () => setView('skip') },
        { id: 'attendance', label: 'Посещаемость', meta: 'Отметки и статистика', icon: orbIcon('attendance'), run: () => setView('attendance') },
      ],
    },
    campus: {
      kicker: 'Кампус',
      title: 'Куда идти?',
      items: [
        { id: 'navigator', label: 'Навигатор', meta: 'Корпуса, аудитории и маршруты', icon: orbIcon('pin'), run: () => setView('navigator') },
      ],
    },
    community: {
      kicker: 'Сообщество',
      title: 'Люди и университет',
      items: [
        { id: 'group', label: 'Моя группа', meta: 'Участники, друзья и приглашения', icon: orbIcon('people'), run: () => setView('group') },
        { id: 'events', label: 'Мероприятия', meta: 'Встречи и события', icon: orbIcon('star'), run: () => setView('events') },
        { id: 'council', label: 'Студсовет', meta: 'Обращения и ответы', icon: orbIcon('message'), run: () => setView('council') },
        { id: 'teachers', label: 'Преподаватели', meta: 'Отзывы, заметки и файлы', icon: orbIcon('teacher'), run: () => setView('teachers') },
        { id: 'wiki', label: 'Wiki', meta: 'Студенческие инструкции и памятки', icon: orbIcon('book'), run: () => setView('wiki') },
      ],
    },
  };
  const config = sections[section];
  if (!config) return;
  const items = config.items.map((item) => ({ ...item, stayInGlass: false }));
  const dialog = $('#dialog');
  const canMorph = dialog?.open && dialog.classList.contains('sg-glass-menu') && dialog.querySelector('.sg-orb-sheet-panel');
  if (canMorph) {
    morphGlassMenu({ kicker: config.kicker, title: config.title, items, showOrb: true, fromEl });
  } else {
    openGlassMenu({ kicker: config.kicker, title: config.title, items, showOrb: true });
  }
}

export async function openSuperGrokOrb() {
  captureGlassOrigin($('#sg-orb') || document.querySelector('.sg-orb'));
  try { if (navigator.vibrate) navigator.vibrate(8); } catch { /* optional haptic */ }
  const items = [];
  if (canManageGroup() || canUploadNotes() || canManageEvents()) {
    items.push({
      id: 'add',
      label: 'Добавить',
      meta: 'Задание, материал, конспект…',
      icon: orbIcon('plus'),
      stayInGlass: true,
      run: (el) => openSimpleAddMenu(el),
    });
  }
  items.push(
    { id: 'study', label: 'Учёба', meta: 'Задания, решения, конспекты, материалы', icon: orbIcon('book'), stayInGlass: true, run: (el) => openOrbSection('study', el) },
    { id: 'schedule-menu', label: 'Расписание', meta: 'Пары, пропуски и посещаемость', icon: orbIcon('calendar'), stayInGlass: true, run: (el) => openOrbSection('schedule', el) },
    { id: 'campus', label: 'Кампус', meta: 'Навигатор по корпусам', icon: orbIcon('pin'), stayInGlass: true, run: (el) => openOrbSection('campus', el) },
    { id: 'community', label: 'Сообщество', meta: 'Группа, события, Wiki, преподаватели', icon: orbIcon('people'), stayInGlass: true, run: (el) => openOrbSection('community', el) },
  );
  openGlassMenu({ kicker: 'Быстрые действия', title: 'Что нужно?', items, showOrb: true });
}

export /** Telegram-style sheet dismiss with spring-out animation. */
function closeOrbSheet() {
  const dialog = $('#dialog');
  if (!dialog?.open) return Promise.resolve();
  if (!dialog.classList.contains('sg-orb-dialog') && !dialog.classList.contains('sg-sheet-dialog')) {
    closeDialog();
    return Promise.resolve();
  }
  setGlassFlowHold(false);
  setGlassFlow(false);
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      if (dialog.open) dialog.close();
      resolve();
    };
    dialog.classList.remove('sg-sheet-open');
    dialog.classList.add('sg-sheet-closing');
    $('#sg-orb')?.classList.remove('is-open');
    document.body.classList.remove('sg-sheet-active');
    const shell = dialog.querySelector('.dialog-shell') || dialog;
    const onEnd = (e) => {
      if (e.target !== shell && e.target !== dialog) return;
      shell.removeEventListener('transitionend', onEnd);
      dialog.classList.remove('sg-sheet-closing');
      finish();
    };
    shell.addEventListener('transitionend', onEnd);
    // Wait for reverse-stagger of action rows + sheet fade.
    setTimeout(finish, 460);
  });
}

function wireOrbSheetGesture(dialog) {
  const grab = dialog?.querySelector('[data-sg-sheet-grab]');
  const panel = dialog?.querySelector('.sg-orb-sheet-panel') || dialog?.querySelector('.dialog-shell');
  if (!grab || !panel || !dialog) return;
  let startY = 0;
  let dragY = 0;
  let tracking = false;
  const onStart = (e) => {
    tracking = true;
    dragY = 0;
    startY = (e.touches ? e.touches[0].clientY : e.clientY) || 0;
    dialog.classList.add('sg-sheet-drag');
  };
  const onMove = (e) => {
    if (!tracking) return;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) || 0;
    dragY = Math.max(0, y - startY);
    dialog.style.setProperty('--sg-sheet-y', `${dragY}px`);
    if (e.cancelable && dragY > 4) e.preventDefault();
  };
  const onEnd = () => {
    if (!tracking) return;
    tracking = false;
    dialog.classList.remove('sg-sheet-drag');
    if (dragY > 96) {
      closeOrbSheet();
    } else {
      dialog.style.setProperty('--sg-sheet-y', '0px');
      requestAnimationFrame(() => dialog.style.removeProperty('--sg-sheet-y'));
    }
    dragY = 0;
  };
  grab.addEventListener('pointerdown', (e) => {
    grab.setPointerCapture?.(e.pointerId);
    onStart(e);
  });
  grab.addEventListener('pointermove', onMove);
  grab.addEventListener('pointerup', onEnd);
  grab.addEventListener('pointercancel', onEnd);
}

export function applyInterfaceMode(mode) {
  const normalized = normalizeInterfaceMode(mode);
  document.body.classList.toggle('interface-simple', normalized === 'simple');
  document.body.classList.toggle('interface-classic', normalized === 'classic');
  document.body.classList.toggle('interface-context', normalized === 'context');
  document.body.classList.toggle('interface-supergrok', normalized === 'supergrok');
  document.body.dataset.interfaceMode = normalized;
  ensureSuperGrokChrome();
  syncSuperGrokChrome();
}

function applyInterfaceStyle(style) {
  const normalized = normalizeInterfaceStyle(style);
  void ensureInterfaceStyle(normalized).catch((error) => console.warn('optional interface style failed to load', normalized, error));
  document.body.classList.toggle('style-bento', normalized === 'bento');
  document.body.classList.toggle('style-night', normalized === 'night');
  document.body.classList.toggle('style-kawaii', normalized === 'kawaii');
  document.body.classList.toggle('style-tor', normalized === 'tor');
  document.body.classList.toggle('style-khokhloma', normalized === 'khokhloma');
  document.body.classList.toggle('style-rzd', normalized === 'rzd');
  document.body.classList.toggle('style-gzhel', normalized === 'gzhel');
  document.body.dataset.interfaceStyle = normalized;
  applyTheme(resolveTheme(currentThemePreference()));
  applyChronicleInitials();
}

export function applyInterfacePreferences(value) {
  state.interfacePreferences = normalizeInterfacePreferences(value);
  setThemePreference(state.interfacePreferences.theme, true);
  applyNavigationOrder(state.interfacePreferences.navigation_order);
  applyAccentColor(state.interfacePreferences.accent_color);
  applyInterfaceMode(state.interfacePreferences.interface_mode);
  applyInterfaceStyle(state.interfacePreferences.interface_style);
  try { localStorage.setItem('kuni-last-interface-mode', state.interfacePreferences.interface_mode); } catch { /* ignore */ }
}

var LETOPIS_SKIP = '.letopis-skip,.brand-wordmark,.sg-dock,.kuni-quick,.nav,.mobile-nav,.icon-button,.button,.pill,time,code,pre,kbd,.campus-navigator,.campus-map-shell,[data-no-letopis],.sg-orb,input,textarea,select,option,label.auth-group-search,.global-search-field';

var LETOPIS_SKIP_TAGS = /^(SCRIPT|STYLE|TEXTAREA|INPUT|SELECT|BUTTON|SVG|CODE|PRE|KBD|OPTION|CANVAS|NOSCRIPT)$/;

var LETOPIS_LETTER = /[A-ZÀ-ÖØ-ÞА-ЯЁ]/;

function chronicleInitialsActive() {
  const prefs = normalizeInterfacePreferences(state.interfacePreferences || {});
  return Boolean(prefs.chronicle_initials && isRussianInterfaceStyle(prefs.interface_style));
}

function unwrapLetopisInitials(root = document) {
  $$('.letopis-wrap', root).forEach((wrap) => {
    wrap.replaceWith(document.createTextNode(wrap.textContent || ''));
  });
}

function shouldSkipLetopis(node) {
  const el = node.nodeType === 1 ? node : node.parentElement;
  if (!el || !el.closest) return true;
  if (el.closest(LETOPIS_SKIP)) return true;
  if (LETOPIS_SKIP_TAGS.test(el.tagName)) return true;
  if (el.closest('.letopis-wrap')) return true;
  return false;
}

function decorateLetopisText(text) {
  if (!text || !LETOPIS_LETTER.test(text)) return null;
  const re = /(?:^[\s«"„'(…]*|[.!?…]["»”')\]]*\s+)([A-ZÀ-ÖØ-ÞА-ЯЁ])/g;
  let html = '';
  let last = 0;
  let match;
  while ((match = re.exec(text))) {
    const letterIndex = match.index + match[0].length - 1;
    if (letterIndex < last) continue;
    html += escapeHTML(text.slice(last, letterIndex));
    html += `<span class="letopis-initial">${escapeHTML(text.charAt(letterIndex))}</span>`;
    last = letterIndex + 1;
    re.lastIndex = last;
  }
  if (!last) return null;
  html += escapeHTML(text.slice(last));
  return html;
}

function decorateLetopisInitials(root = document) {
  if (!root || !chronicleInitialsActive()) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || !LETOPIS_LETTER.test(node.nodeValue)) return NodeFilter.FILTER_REJECT;
      if (shouldSkipLetopis(node)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach((node) => {
    const html = decorateLetopisText(node.nodeValue);
    if (!html) return;
    const wrap = document.createElement('span');
    wrap.className = 'letopis-wrap';
    wrap.innerHTML = html;
    node.replaceWith(wrap);
  });
}

export function applyChronicleInitials() {
  const content = $('#content');
  const keepY = content ? content.scrollTop : 0;
  const on = chronicleInitialsActive();
  document.body.classList.toggle('chronicle-initials', on);
  unwrapLetopisInitials(document);
  if (on) {
    decorateLetopisInitials($('#content') || document);
    decorateLetopisInitials($('#dialog-body'));
    decorateLetopisInitials($('#dialog-title'));
    decorateLetopisInitials($('#legal-document'));
    decorateLetopisInitials($('.workspace-heading'));
  }
  if (content) content.scrollTop = keepY;
}

export async function saveAppearancePreferences(patch, successMessage = 'Настройки сохранены') {
  const before = normalizeInterfacePreferences(state.interfacePreferences);
  const candidate = normalizeInterfacePreferences({ ...before, ...patch });
  const content = $('#content');
  const keepY = content ? content.scrollTop : 0;
  applyInterfacePreferences(candidate);
  try {
    const saved = await api('/interface-preferences', { method: 'PATCH', body: JSON.stringify(candidate) });
    const merged = { ...candidate, ...(saved || {}) };
    if (saved && saved.chronicle_initials == null) merged.chronicle_initials = candidate.chronicle_initials;
    applyInterfacePreferences(merged);
    saveLocalInterfacePreferences(state.interfacePreferences);
    renderSectionTabs();
    requestAnimationFrame(() => { if (content) content.scrollTop = keepY; });
    if (successMessage) toast(successMessage);
    return state.interfacePreferences;
  } catch (error) {
    applyInterfacePreferences(before);
    renderSectionTabs();
    requestAnimationFrame(() => { if (content) content.scrollTop = keepY; });
    toast(error?.message || 'Не удалось сохранить настройки');
    throw error;
  }
}

function openPersonalizationSettings() {
  return setView('settings');
}

export async function openTelegramBot() {
  const button = $('#telegram-shortcut');
  if (button?.disabled) return;
  if (button) button.disabled = true;
  try {
    const result = await api('/telegram/link-code', { method: 'POST' });
    const deepLink = String(result?.deep_link || '').trim();
    if (!/^https:\/\/t\.me\/[A-Za-z0-9_]{5,32}\?start=link_[A-Za-z0-9_-]+$/.test(deepLink)) {
      throw new Error('Telegram-бот не настроен: укажите TELEGRAM_BOT_USERNAME в .env и перезапустите сервис');
    }
    window.location.assign(deepLink);
  } finally {
    if (button) button.disabled = false;
  }
}

function globalSearchKindLabel(kind) {
  return ({schedule:'Пара',homework:'ДЗ',requirement:'Автомат',material:'Материал',note:'Конспект',solution:'Решение',group:'Группа',view:'Раздел'})[kind] || 'Результат';
}

function globalSearchKindGlyph(kind) {
  return ({schedule:'▦',homework:'✓',requirement:'○',material:'↗',note:'≡',solution:'✓',group:'⌂',view:'☰'})[kind] || '•';
}

function navSearchCatalog() {
  const study = {semester:'Семестр',courses:'Предметы',homework:'Задания',requirements:'Автомат',notes:'Конспекты',solutions:'Решения',materials:'Материалы',attendance:'Посещаемость',skip:'Пропуск пары'};
  const more = {teachers:'Преподаватели',wiki:'Wiki',events:'Мероприятия',council:'Студсовет',notifications:'Уведомления',group:'Моя группа',navigator:'Навигатор',admin:'Админка'};
  const items = [
    {view:'home', title:'Главная', subtitle:'Сейчас и ближайшие дела', hub:'Главная'},
    {view:'schedule', title:'Расписание', subtitle:'Пары на неделю', hub:'Пары'},
    {view:'studyhub', title:'Учёба', subtitle:'Семестр, задания, автомат', hub:'Учёба'},
    {view:'morehub', title:'Ещё', subtitle:'Кампус, группа, настройки', hub:'Ещё'},
    {view:'settings', title:'Настройки', subtitle:'Тема, стиль, аккаунт', hub:'Ещё'},
  ];
  Object.entries(study).forEach(([view, title]) => items.push({view, title, subtitle:'Подменю учёбы', hub:'Учёба'}));
  Object.entries(more).forEach(([view, title]) => {
    if (view === 'admin' && !isAdmin()) return;
    items.push({view, title, subtitle:'Подменю «Ещё»', hub:'Ещё'});
  });
  return items;
}

export async function openGlobalSearch() {
  if (state.isGuest) { toast('Поиск доступен после входа'); return; }
  const emptyState = `<div class="global-search-empty">
      <strong>Ищи как думаешь</strong>
      <span>Раздел меню, пары, ДЗ, конспекты — одной строкой.</span>
    </div>`;
  openDialog('Поиск', `<div class="global-search-shell">
    <label class="global-search-field"><span class="global-search-icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg></span><input id="global-search-input" autocomplete="off" enterkeyhint="search" placeholder="Раздел, предмет, дз, группа…"></label>
    <div class="global-search-hints"><button type="button" data-search-example="учёба">учёба</button><button type="button" data-search-example="настройки">настройки</button><button type="button" data-search-example="дз матан">дз матан</button><button type="button" data-search-example="группа">группа</button></div>
    <div id="global-search-results">${emptyState}</div>
  </div>`, [{label:'Закрыть',action:closeDialog}]);
  const dialog = $('#dialog');
  dialog?.classList.add('global-search-dialog');
  const kicker = dialog?.querySelector('.dialog-kicker');
  if (kicker) kicker.hidden = true;
  const title = $('#dialog-title');
  if (title) title.textContent = 'Поиск';
  const closeBtn = $('#dialog-close');
  if (closeBtn) closeBtn.hidden = false;
  const input=$('#global-search-input'); const results=$('#global-search-results'); let timer=0; let serial=0;
  const run=async()=>{
    const q=input.value.trim(); const mySerial=++serial; clearTimeout(timer);
    if(!q){results.innerHTML=emptyState;return;}
    const needle=q.toLowerCase();
    const menuHits=navSearchCatalog().filter((item)=>`${item.title} ${item.subtitle} ${item.hub} ${item.view}`.toLowerCase().includes(needle)).map((item)=>({kind:'view',id:item.view,title:item.title,subtitle:`${item.hub} · ${item.subtitle}`}));
    const groupHits=(state.groups||[]).filter((g)=>`${g.name||''} ${g.university_name||''} ${g.institute_name||''}`.toLowerCase().includes(needle)).map((g)=>({kind:'group',id:g.id,title:g.name||'Группа',subtitle:[g.university_name,g.institute_name].filter(Boolean).join(' · ')}));
    if(!state.groupID){
      const merged=[...menuHits,...groupHits];
      results.innerHTML=merged.length?`<div class="global-search-list">${merged.map((item)=>`<button class="global-search-result" type="button" data-kind="${escapeHTML(item.kind)}" data-id="${item.id}"><span class="global-search-glyph" aria-hidden="true">${globalSearchKindGlyph(item.kind)}</span><div><small>${globalSearchKindLabel(item.kind)}</small><strong>${escapeHTML(item.title)}</strong><p>${escapeHTML(item.subtitle||'')}</p></div><b aria-hidden="true">→</b></button>`).join('')}</div>`:'<div class="global-search-empty"><strong>Ничего не нашлось</strong><span>Попробуй название раздела: учёба, задания, настройки.</span></div>';
      $$('.global-search-result',results).forEach((button)=>button.addEventListener('click',()=>openGlobalSearchResult(button.dataset.kind,button.dataset.id)));
      return;
    }
    results.innerHTML='<div class="lesson-picker-loading"><strong>Ищу</strong><span>Разделы, расписание и материалы группы</span></div>';
    try{
      const items=await api(`/groups/${state.groupID}/search?q=${encodeURIComponent(q)}&limit=50`); if(mySerial!==serial)return; trackProduct('search.used',{results:(items||[]).length+menuHits.length});
      const merged=[...menuHits,...groupHits,...(items||[])];
      results.innerHTML=merged.length?`<div class="global-search-list">${merged.map((item)=>`<button class="global-search-result" type="button" data-kind="${escapeHTML(item.kind)}" data-id="${item.id}" data-entry="${item.schedule_entry_id||''}" data-url="${escapeHTML(item.url||'')}"><span class="global-search-glyph" aria-hidden="true">${globalSearchKindGlyph(item.kind)}</span><div><small>${globalSearchKindLabel(item.kind)}</small><strong>${escapeHTML(item.title)}</strong><p>${escapeHTML(item.subtitle||item.body||'')}</p></div><b aria-hidden="true">→</b></button>`).join('')}</div>`:'<div class="global-search-empty"><strong>Ничего не нашлось</strong><span>Попробуй короче: раздел, предмет, тип, группу или дату.</span></div>';
      $$('.global-search-result',results).forEach((button)=>button.addEventListener('click',()=>openGlobalSearchResult(button.dataset.kind,button.dataset.id,button.dataset.url,button.dataset.entry)));
    }catch(error){if(mySerial===serial)results.innerHTML=`<div class="global-search-empty"><strong>Поиск недоступен</strong><span>${escapeHTML(error.message)}</span></div>`;}
  };
  input.addEventListener('input',()=>{clearTimeout(timer);timer=setTimeout(run,170);});
  $$('[data-search-example]').forEach((button)=>button.addEventListener('click',()=>{input.value=button.dataset.searchExample;run();input.focus();}));
  requestAnimationFrame(()=>input.focus());
}

async function openGlobalSearchResult(kind,id,rawURL='',scheduleEntryID='') {
  closeDialog();
  if(kind==='view'){await setView(id);return;}
  if(kind==='group'){
    if(id && id!==state.groupID){
      state.groupID=id;
      try{localStorage.setItem(state.isGuest?'kuni-guest-group-id':'kuni-group-id',id);}catch{/* ignore */}
      renderGroupSelector();
    }
    await setView('group');
    return;
  }
  if(kind==='schedule'){await showSchedule(id);return;}
  if(kind==='note'||kind==='solution'){await openNote(id);return;}
  if((kind==='homework'||kind==='requirement')&&scheduleEntryID){await showSchedule(scheduleEntryID);return;}
  if(kind==='material' && /^https?:\/\//i.test(rawURL)){window.open(rawURL,'_blank','noopener,noreferrer');return;}
  if(kind==='homework'){await setView('homework');return;}
  if(kind==='requirement'){await setView('requirements');return;}
  if(kind==='material'){await setView('materials');return;}
}

var KUNI_QUICK_POS_KEY = 'kuni-quick-pos';

var kuniQuickOpen = false;

var kuniQuickDrag = { active: false, moved: false, pointerId: 0, startX: 0, startY: 0, origX: 0, origY: 0 };

function kuniQuickIsOpen() { return kuniQuickOpen; }

function loadKuniQuickPos() {
  try {
    const raw = JSON.parse(localStorage.getItem(KUNI_QUICK_POS_KEY) || '');
    if (raw && Number.isFinite(raw.x) && Number.isFinite(raw.y)) return { x: raw.x, y: raw.y };
  } catch { /* ignore */ }
  return null;
}

function saveKuniQuickPos(pos) {
  try { localStorage.setItem(KUNI_QUICK_POS_KEY, JSON.stringify(pos)); } catch { /* ignore */ }
}

function kuniQuickBottomReserve() {
  const vh = window.innerHeight;
  const dock = $('#sg-dock');
  if (dock && !dock.hidden) {
    const top = dock.getBoundingClientRect().top;
    if (Number.isFinite(top)) return Math.max(16, vh - top + 12);
  }
  const mobile = $('.mobile-nav');
  if (mobile && getComputedStyle(mobile).display !== 'none') return 86;
  return 16;
}

function clampKuniQuickPos(x, y) {
  const size = 56;
  const pad = 10;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const maxX = Math.max(pad, vw - size - pad);
  const maxY = Math.max(pad, vh - size - kuniQuickBottomReserve());
  return {
    x: Math.min(maxX, Math.max(pad, x)),
    y: Math.min(maxY, Math.max(pad, y)),
  };
}

function applyKuniQuickPos(pos) {
  const root = $('#kuni-quick');
  if (!root) return;
  if (!pos) {
    root.style.left = '';
    root.style.top = '';
    root.style.right = '';
    root.style.bottom = '';
    return;
  }
  const next = clampKuniQuickPos(pos.x, pos.y);
  root.style.left = `${Math.round(next.x)}px`;
  root.style.top = `${Math.round(next.y)}px`;
  root.style.right = 'auto';
  root.style.bottom = 'auto';
}

function updateKuniQuickFlip() {
  const root = $('#kuni-quick');
  if (!root || root.hidden) return;
  const rect = root.getBoundingClientRect();
  root.classList.toggle('is-down', rect.top < 148);
}

export function setKuniQuickOpen(open) {
  const root = $('#kuni-quick');
  const main = $('#global-search-button');
  kuniQuickOpen = Boolean(open) && root && !root.hidden;
  if (!root) return kuniQuickOpen;
  root.classList.toggle('is-open', kuniQuickOpen);
  if (main) {
    main.setAttribute('aria-expanded', kuniQuickOpen ? 'true' : 'false');
    main.setAttribute('aria-label', kuniQuickOpen ? 'Поиск' : 'Меню');
    main.title = kuniQuickOpen ? 'Поиск' : 'Меню';
  }
  $('#telegram-shortcut')?.setAttribute('aria-hidden', kuniQuickOpen ? 'false' : 'true');
  $('#help-onboarding-button')?.setAttribute('aria-hidden', kuniQuickOpen ? 'false' : 'true');
  updateKuniQuickFlip();
  return kuniQuickOpen;
}

export function syncKuniQuick() {
  const root = $('#kuni-quick');
  if (!root) return;
  const appOn = Boolean($('#app-view') && !$('#app-view').hidden);
  root.hidden = !appOn || Boolean(state.isGuest);
  if (root.hidden) {
    setKuniQuickOpen(false);
    return;
  }
  applyKuniQuickPos(loadKuniQuickPos());
  updateKuniQuickFlip();
}

function initKuniQuick() {
  const root = $('#kuni-quick');
  const cluster = $('#kuni-quick-cluster');
  const main = $('#global-search-button');
  if (!root || !cluster || !main || root.dataset.ready) return;
  root.dataset.ready = '1';
  applyKuniQuickPos(loadKuniQuickPos());
  syncKuniQuick();

  $('#kuni-quick-scrim')?.addEventListener('click', () => setKuniQuickOpen(false));

  cluster.addEventListener('pointerdown', (event) => {
    if (event.button != null && event.button !== 0) return;
    const rect = root.getBoundingClientRect();
    kuniQuickDrag.active = true;
    kuniQuickDrag.moved = false;
    kuniQuickDrag.pointerId = event.pointerId;
    kuniQuickDrag.startX = event.clientX;
    kuniQuickDrag.startY = event.clientY;
    kuniQuickDrag.origX = rect.left;
    kuniQuickDrag.origY = rect.top;
    try { cluster.setPointerCapture(event.pointerId); } catch { /* ignore */ }
  });

  cluster.addEventListener('pointermove', (event) => {
    if (!kuniQuickDrag.active || event.pointerId !== kuniQuickDrag.pointerId) return;
    const dx = event.clientX - kuniQuickDrag.startX;
    const dy = event.clientY - kuniQuickDrag.startY;
    if (!kuniQuickDrag.moved && (dx * dx + dy * dy) < 64) return;
    if (!kuniQuickDrag.moved) {
      kuniQuickDrag.moved = true;
      root.classList.add('is-dragging');
    }
    applyKuniQuickPos({ x: kuniQuickDrag.origX + dx, y: kuniQuickDrag.origY + dy });
    updateKuniQuickFlip();
    if (event.cancelable) event.preventDefault();
  });

  const endDrag = (event) => {
    if (!kuniQuickDrag.active || (event && event.pointerId !== kuniQuickDrag.pointerId)) return;
    const moved = kuniQuickDrag.moved;
    kuniQuickDrag.active = false;
    root.classList.remove('is-dragging');
    if (moved) {
      const rect = root.getBoundingClientRect();
      const pos = clampKuniQuickPos(rect.left, rect.top);
      applyKuniQuickPos(pos);
      saveKuniQuickPos(pos);
      updateKuniQuickFlip();
    }
    kuniQuickDrag.moved = moved;
    setTimeout(() => { kuniQuickDrag.moved = false; }, 0);
  };
  cluster.addEventListener('pointerup', endDrag);
  cluster.addEventListener('pointercancel', endDrag);

  main.addEventListener('click', (event) => {
    if (kuniQuickDrag.moved) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    if (!kuniQuickIsOpen()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      setKuniQuickOpen(true);
      return;
    }
    setKuniQuickOpen(false);
    openGlobalSearch().catch((error) => toast(error.message));
  });

  window.addEventListener('resize', () => {
    if ($('#kuni-quick')?.hidden) return;
    const saved = loadKuniQuickPos();
    if (saved) applyKuniQuickPos(saved);
    updateKuniQuickFlip();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && kuniQuickIsOpen()) {
      event.preventDefault();
      setKuniQuickOpen(false);
    }
  });
}

async function changeGroup(event) {
  const id = event.target.value;
  if (await selectGroup(id)) toast(`Группа: ${currentGroupLabel()}`, 'ok');
}

export function renderSectionTabs() {
  const settingsActive = state.view === 'settings';
  const standaloneAdminPage = state.view === 'ui_text_editor';
  $$('.global-settings-item, .global-settings-mobile').forEach((button) => button.classList.toggle('active', settingsActive));
  if (settingsActive || standaloneAdminPage) {
    const nav = $('#section-tabs');
    nav.hidden = true;
    nav.innerHTML = '';
    $$('.section-nav-item,.simple-nav-item,[data-mobile-section],[data-simple-mobile-view]').forEach((button) => button.classList.remove('active'));
    return;
  }
  if (state.isGuest && contextMode()) {
    const nav = $('#section-tabs');
    nav.hidden = true;
    nav.innerHTML = '';
    const sectionID = sectionForView(state.view);
    $$('.section-nav-item').forEach((button)=>button.classList.toggle('active',button.dataset.section===sectionID));
    $$('[data-mobile-section]').forEach((button)=>button.classList.toggle('active',button.dataset.mobileSection===sectionID));
    $$('.simple-nav-item,[data-simple-mobile-view]').forEach((button)=>button.classList.remove('active'));
    return;
  }
  if (compactMode()) {
    const parent = simpleParentView(state.view);
    const views = availableSimpleViews(parent);
    const nav = $('#section-tabs');
    // SuperGrok: dock + hub lists are the IA — classic section chips fight the scheme (11.png).
    const hideHubTabs = superGrokMode();
    nav.innerHTML = hideHubTabs ? '' : views.map(([view,label]) => `<button type="button" class="section-tab ${view===state.view?'active':''}" data-section-view="${view}">${escapeHTML(t(label))}</button>`).join('');
    nav.hidden = hideHubTabs || views.length === 0;
    $$('[data-section-view]', nav).forEach((button)=>{button.addEventListener('click',()=>setView(button.dataset.sectionView));button.addEventListener('pointerenter',()=>{void preloadView(button.dataset.sectionView);},{once:true});button.addEventListener('focus',()=>{void preloadView(button.dataset.sectionView);},{once:true});});
    $$('.simple-nav-item').forEach((button)=>button.classList.toggle('active',button.dataset.simpleView===parent));
    $$('[data-simple-mobile-view]').forEach((button)=>button.classList.toggle('active',button.dataset.simpleMobileView===parent));
    return;
  }
  const sectionID = sectionForView(state.view);
  const views = availableSectionViews(sectionID);
  const nav = $('#section-tabs');
  nav.hidden = false;
  nav.innerHTML = views.map(([view,label]) => `<button type="button" class="section-tab ${view===state.view?'active':''}" data-section-view="${view}">${escapeHTML(t(label))}</button>`).join('');
  $$('[data-section-view]', nav).forEach((button)=>{button.addEventListener('click',()=>setView(button.dataset.sectionView));button.addEventListener('pointerenter',()=>{void preloadView(button.dataset.sectionView);},{once:true});button.addEventListener('focus',()=>{void preloadView(button.dataset.sectionView);},{once:true});});
  $$('.section-nav-item').forEach((button)=>button.classList.toggle('active',button.dataset.section===sectionID));
  $$('[data-mobile-section]').forEach((button)=>button.classList.toggle('active',button.dataset.mobileSection===sectionID));
}

export async function setView(view, opts = {}) {
  const replaceURL = opts.replace === true;
  const syncURL = opts.syncURL !== false;
  if (state.isGuest && view !== 'schedule' && view !== 'navigator') view = 'schedule';
  if (!compactMode() && ['home','studyhub','morehub','lesson'].includes(view)) view = 'schedule';
  if (view === 'admin' && !isAdmin()) view = 'notifications';
  if (view === 'ui_text_editor' && !isProjectAdmin()) view = compactMode() ? 'home' : 'schedule';
  const prev = state.view;
  await preloadView(view);
  state.view = view;
  document.body.dataset.view = view;
  if (syncURL) syncViewURL(view, { replace: replaceURL || prev === view });
  renderSectionTabs();
  await renderView();
  const content = $('#content');
  content?.scrollTo({ top: 0, behavior: 'auto' });
  // SuperGrok hubs: light fade-in so swaps feel intentional, not a hard cut.
  if (superGrokMode() && content && prev !== view) {
    content.classList.remove('sg-hub-fade');
    void content.offsetWidth;
    content.classList.add('sg-hub-fade');
  }
  if (view === 'schedule') trackProduct('schedule.opened');
  if (view === 'requirements') trackProduct('requirement.opened');
}

async function handleRouteChange() {
  if ($('#auth-view') && !$('#auth-view').hidden && $('#app-view')?.hidden) return;
  if ($('#legal-view') && !$('#legal-view').hidden) return;
  const link = parseDeepLink();
  state.deepLink = link;
  setRouterSilent(true);
  try {
    if (link.type === 'legal') {
      await showLegalPage(link.document);
      return;
    }
    if (link.type === 'view' && Object.prototype.hasOwnProperty.call(titleKeys, link.view)) {
      await setView(link.view, { syncURL: false });
      return;
    }
    if (link.type === 'course') {
      state.courseSubjectID = link.id;
      await setView('course', { syncURL: false });
      return;
    }
    if (link.type === 'lesson') {
      await setView(compactMode() || superGrokMode() || contextMode() ? 'lesson' : 'schedule', { syncURL: false });
      goToScheduleEntry(link.id).catch((error) => toast(error.message));
      return;
    }
    if (link.type === 'note') {
      await setView('notes', { syncURL: false });
      openNote(link.id).catch((error) => toast(error.message));
      return;
    }
    if (link.type === 'homework') {
      await setView('homework', { syncURL: false });
      focusResourceDeepLink(link.id);
      return;
    }
    if (link.type === 'requirement') {
      await setView('requirements', { syncURL: false });
      focusResourceDeepLink(link.id);
      return;
    }
    if (link.type === 'material') {
      await setView('materials', { syncURL: false });
      focusResourceDeepLink(link.id);
      return;
    }
    if (link.type === 'none' || link.type === 'join') {
      const fallback = compactMode() ? 'home' : 'schedule';
      if (state.view !== fallback) await setView(fallback, { syncURL: false });
    }
  } finally {
    setRouterSilent(false);
  }
}

export function resetHeadActions() { $('#head-actions').replaceChildren(); }

export function headAction(label, handler, className = 'button primary small') {
  const button = document.createElement('button');
  button.type = 'button'; button.className = className; button.textContent = label;
  button.addEventListener('click', async () => {
    try { await handler(); }
    catch (error) { console.error(error); toast(error?.message || t('common.error.action')); }
  });
  $('#head-actions').append(button);
}

export async function renderView() {
  const [title, subtitle] = titleFor(state.view);
  publishViewSnapshot({ view: state.view, title, subtitle, phase: 'rendering', error: '' });
  document.title = `${title} · kUni`;
  resetHeadActions();
  const content = $('#content');
  content.style.transition = 'none';
  content.style.transform = '';
  content.style.opacity = '';
  content.innerHTML = (contextMode() || superGrokMode()) ? contextSkeleton() : `<div class="empty">${escapeHTML(t('common.loading'))}</div>`;
  try {
    const standardRenderers = {
      home: renderSimpleHome, studyhub: renderSimpleStudyHub, morehub: renderSimpleMoreHub,
      schedule: renderSchedule, skip: renderSkipLesson, homework: renderHomework, requirements: renderRequirements,
      materials: renderMaterials, attendance: renderAttendance, wiki: renderWiki, teachers: renderTeachers, notes: renderNotes, solutions: renderSolutions, navigator: renderNavigator, events: renderEvents, council: renderStudentCouncil, notifications: renderNotifications, settings: renderSettings,
      semester: renderSemester, courses: renderCourses, course: renderCourseSpace, lesson: renderContextLesson,
      group: renderGroup, admin: renderAdmin, ui_text_editor: renderUITextEditor,
    };
    const contextRenderers = {
      home: renderContextHome, studyhub: renderContextStudyHub, morehub: renderContextMoreHub,
      schedule: renderContextSchedule, semester: renderContextSemester, courses: renderContextCourses,
      course: renderContextCourseSpace, lesson: renderContextLesson,
    };
    const superGrokRenderers = {
      home: renderSuperGrokHome, studyhub: renderSuperGrokStudyHub, morehub: renderSuperGrokMoreHub,
      schedule: renderSuperGrokSchedule, semester: renderContextSemester, courses: renderContextCourses,
      course: renderContextCourseSpace, lesson: renderContextLesson,
    };
    const renderer = (superGrokMode() ? superGrokRenderers[state.view] : null)
      || (contextMode() ? contextRenderers[state.view] : null)
      || standardRenderers[state.view]
      || renderSchedule;
    await renderer();
    if (superGrokMode()) {
      const page = $('#content > *');
      // Don't stamp sg-page on the campus map root — it breaks full-bleed layout.
      if (page && !page.classList.contains('sg-home') && !page.classList.contains('campus-navigator')) {
        page.classList.add('sg-page');
      }
    }
    publishViewSnapshot({ view: state.view, title, subtitle, phase: 'ready', error: '' });
  } catch (error) {
    console.error('view render failed',state.view,error);
    publishViewSnapshot({ view: state.view, title, subtitle, phase: 'error', error: error?.message || 'Неизвестная ошибка' });
    $('#content').innerHTML = `<div class="empty v12-view-error"><span>!</span><strong>${escapeHTML(t('common.error.section'))}</strong><p>${escapeHTML(error.message||'Неизвестная ошибка')}</p>${error.requestID ? `<small>request id · ${escapeHTML(error.requestID)}</small>` : ''}<button class="button primary small" type="button" data-retry-view>Повторить без перезагрузки</button></div>`;
    $('[data-retry-view]')?.addEventListener('click',()=>renderView());
  }
  syncSuperGrokChrome();
  mountWindowSwipe();
  applyChronicleInitials();
}

export function requireGroup() {
  if (!state.groupID) {
    $('#content').innerHTML = state.isGuest
      ? '<div class="empty"><strong>Нет доступных групп</strong>Активные группы появятся здесь после публикации.</div>'
      : '<div class="empty"><strong>Сначала вступите в группу</strong>Откройте раздел «Группа» и введите код приглашения.</div>';
    return false;
  }
  return true;
}

export async function loadCatalogs(force = false) {
  if (!force && state.catalogs.subjects.length) return;
  const [subjects, teachers, rooms] = await Promise.all([
    api('/catalog/subjects'), api('/catalog/teachers'), api('/catalog/rooms'),
  ]);
  state.catalogs = { subjects: subjects || [], teachers: teachers || [], rooms: rooms || [] };
}

export var options = (items, valueKey, label) => items.map((item) => `<option value="${item[valueKey]}">${escapeHTML(label(item))}</option>`).join('');

export async function loadSubgroups(force = false) {
  if (!state.groupID) return [];
  if (!force && state.subgroups.length) return state.subgroups;
  state.subgroups = await api(`/groups/${state.groupID}/subgroups`) || [];
  return state.subgroups;
}

export function subgroupOptions(selected = '') {
  return `<option value="">Вся группа</option>${state.subgroups.map((v) => `<option value="${v.id}" ${v.id === selected ? 'selected' : ''}>${escapeHTML(v.name)}</option>`).join('')}`;
}

export function mondayFor(offset = 0) {
  const d = new Date(); d.setHours(0,0,0,0);
  const day = d.getDay() || 7; d.setDate(d.getDate() - day + 1 + offset * 7); return d;
}

export function weekOffsetForDate(value) {
  const target = new Date(value); target.setHours(12,0,0,0);
  const targetDay = target.getDay() || 7; target.setDate(target.getDate() - targetDay + 1);
  const current = mondayFor(0); current.setHours(12,0,0,0);
  return Math.round((target - current) / (7 * 86400000));
}

export function focusResourceDeepLink(id) {
  requestAnimationFrame(() => {
    const row = $$('#content [data-id]').find((node) => node.dataset.id === id);
    if (!row) return;
    row.classList.add('resource-row-focused');
    row.scrollIntoView({ behavior:'smooth', block:'center' });
  });
}

export async function goToScheduleEntry(id) {
  if (!id) return;
  trackProduct('lesson.opened');
  if (contextMode() || superGrokMode()) { state.contextEntryID = id; if ($('#dialog').open) closeDialog(); await setView('lesson'); return; }
  try {
    const entry = await api(`/schedule/${id}`);
    state.weekOffset = weekOffsetForDate(entry.starts_at);
    state.scheduleFocusEntryID = id;
    if ($('#dialog').open) closeDialog();
    await setView('schedule');
    requestAnimationFrame(() => {
      const row = $$('.schedule-row').find((node) => node.dataset.entryId === id);
      row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  } catch (error) {
    console.error('Не удалось открыть связанную пару', error);
    toast(error?.message || 'Не удалось открыть пару в расписании');
  }
}

export function dateOnlyLocal(value) {
  const d = new Date(value); const local = new Date(d.getTime() - d.getTimezoneOffset()*60000); return local.toISOString().slice(0,10);
}

export function isoWeekNumber(value) {
  const d = new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
  const day = d.getUTCDay() || 7; d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

export function isToday(value) {
  const a = new Date(value), b = new Date();
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function confirmAction(message) { return window.confirm(message); }

export function isKUniAndroidShell() {
  return typeof window.KUniAndroidNative !== 'undefined'
    || /KUniAndroid\//.test(navigator.userAgent || '')
    || document.documentElement?.dataset?.kuniAndroid === '1';
}

export /** Any Android WebView / in-app browser where blob:<a download> breaks. */
function isAndroidWebClient() {
  const ua = navigator.userAgent || '';
  return /Android/i.test(ua)
    || isKUniAndroidShell()
    || /; wv\)/i.test(ua);
}

export /** API path under /api/v1 (never hit SPA HTML as a “file”). */
function androidDownloadPath(path = '') {
  let p = String(path || '').trim();
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) {
    try {
      const u = new URL(p, window.location.origin);
      p = u.pathname + (u.search || '');
    } catch { return ''; }
  }
  if (!p.startsWith('/')) p = `/${p}`;
  if (p.startsWith('/api/v1/') || p === '/api/v1') return p;
  // rawRequest / API already prefix /api/v1 — avoid double prefix
  if (p.startsWith(API + '/')) return p;
  return `${API}${p}`;
}

export function initNavigationRuntime() {
  $('#telegram-shortcut').addEventListener('click', (event) => {
    if (kuniQuickDrag.moved) {
      event.preventDefault();
      return;
    }
    setKuniQuickOpen(false);
    openTelegramBot().catch((error) => toast(error.message));
  });

  $('#help-onboarding-button')?.addEventListener('click', (event) => {
    if (kuniQuickDrag.moved) {
      event.preventDefault();
      return;
    }
    if (state.isGuest) return;
    setKuniQuickOpen(false);
    openOnboardingWizardV13({ force: true, reason: 'replay' });
  });

  initKuniQuick();

  document.addEventListener('keydown',(event)=>{if((event.ctrlKey||event.metaKey)&&event.key.toLowerCase()==='k'&&!state.isGuest&&$('#app-view')&&!$('#app-view').hidden){event.preventDefault();setKuniQuickOpen(false);openGlobalSearch().catch((error)=>toast(error.message));}});

  $('#group-selector')?.addEventListener($('#group-selector')?.tagName==='SELECT'?'change':'click',(event)=>{if(event.currentTarget.tagName==='SELECT')return changeGroup(event);openGroupSwitcher();});

  $('#guest-group-selector')?.addEventListener($('#guest-group-selector')?.tagName==='SELECT'?'change':'click',(event)=>{if(event.currentTarget.tagName==='SELECT')return changeGroup(event);openGroupSwitcher();});

  window.addEventListener('popstate', () => {
    handleRouteChange().catch((error) => {
      console.error('route change failed', error);
      toast(error?.message || 'Не удалось открыть раздел');
    });
  });
}
